import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { ListItem } from '@/components/ui/ListItem';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useMarkAllRead, useMarkNotificationRead, useNotifications } from '@/api/queries/notification';
import { formatDate } from '@/utils/date';
import { useLocale } from '@/hooks/useLocale';
import { BalanceFormatter } from '@/components/domain/BalanceFormatter';

type Tab = 'payment' | 'system';

export function NotificationsPage(): JSX.Element {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [tab, setTab] = useState<Tab>('payment');
  const list = useNotifications({ tab, limit: 30 });
  const markAll = useMarkAllRead();
  const markOne = useMarkNotificationRead();

  const handleItemClick = (id: string, isRead: boolean) => {
    if (isRead) return;
    markOne.mutate(id);
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('notifications.title')} />
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant={tab === 'payment' ? 'primary' : 'secondary'} onClick={() => setTab('payment')}>
            {t('notifications.tabs.payment')}
          </Button>
          <Button variant={tab === 'system' ? 'primary' : 'secondary'} onClick={() => setTab('system')}>
            {t('notifications.tabs.system')}
          </Button>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" onClick={() => markAll.mutate()}>
            {t('notifications.mark_all_read')}
          </Button>
        </div>

        {list.isLoading && <Spinner />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.data?.items?.map((n) => (
            <ListItem
              key={n.id}
              title={
                n.is_read ? (
                  t(`notifications.kind.${n.kind}`, { defaultValue: n.title ?? n.kind })
                ) : (
                  <span style={{ fontWeight: 700 }}>
                    {t(`notifications.kind.${n.kind}`, { defaultValue: n.title ?? n.kind })}
                  </span>
                )
              }
              subtitle={`${formatDate(n.created_at, locale)}${n.body ? ' · ' + n.body : ''}`}
              onClick={() => handleItemClick(n.id, n.is_read)}
              trailing={
                n.amount_cents !== null && n.amount_cents !== undefined ? (
                  <span>
                    {n.amount_cents >= 0 ? '+' : '-'}
                    <BalanceFormatter cents={Math.abs(n.amount_cents)} />
                  </span>
                ) : null
              }
            />
          ))}
          {!list.isLoading && !list.data?.items?.length && (
            <div style={{ color: 'var(--color-text-on-dark-muted)' }}>{t('notifications.empty')}</div>
          )}
        </div>
      </PageContainer>
    </AppShell>
  );
}
